export type GlifyCssColor = { r: number; g: number; b: number; a?: number }

const DEFAULT: GlifyCssColor = { r: 0.2, g: 0.2, b: 0.2, a: 1 }

/**
 * leaflet.glify `weight` is a brush radius (offsets −w…+w), not Leaflet Path px.
 * Half the px width matches the canvas stroke; the extra scale pulls the hard
 * strip back toward how an antialiased canvas line reads.
 */
const GLIFY_LINE_WIDTH_SCALE = 0.75

export function leafletLineWidthToGlifyWeight(lineWidthPx: number | undefined): number {
  const px = typeof lineWidthPx === 'number' && Number.isFinite(lineWidthPx) ? lineWidthPx : 4
  return Math.max(0.25, (px / 2) * GLIFY_LINE_WIDTH_SCALE)
}

/**
 * Fill radius inside a glify point sprite. The sprite's outer edge is 0.5;
 * the ring from this radius out to 0.5 is the border.
 */
export function glifyPointFillRadius(radiusPx: number, borderWidthPx: number): number {
  const radius = Number.isFinite(radiusPx) && radiusPx > 0 ? radiusPx : 0
  const border = Number.isFinite(borderWidthPx) && borderWidthPx > 0 ? borderWidthPx : 0
  const outer = radius + border
  if (outer <= 0) return 0.5
  return 0.5 * (radius / outer)
}

/** Parse `#rgb` / `#rrggbb` / `rgb()` / `rgba()` into glify 0–1 channels. */
export function cssColorToGlify(color: string | undefined, alpha = 1): GlifyCssColor {
  if (!color || typeof color !== 'string') {
    return { ...DEFAULT, a: alpha }
  }
  const trimmed = color.trim()
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(trimmed)
  if (hex) {
    let h = hex[1]!
    if (h.length === 3) {
      h = h
        .split('')
        .map((c) => c + c)
        .join('')
    }
    const n = Number.parseInt(h, 16)
    return {
      r: ((n >> 16) & 255) / 255,
      g: ((n >> 8) & 255) / 255,
      b: (n & 255) / 255,
      a: alpha,
    }
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i.exec(
    trimmed,
  )
  if (rgb) {
    return {
      r: Number(rgb[1]) / 255,
      g: Number(rgb[2]) / 255,
      b: Number(rgb[3]) / 255,
      a: rgb[4] !== undefined ? Number(rgb[4]) : alpha,
    }
  }
  return { ...DEFAULT, a: alpha }
}
